
import React, { useState, useEffect } from 'react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import { Lock, Unlock, Check, X, Shield, Info } from 'lucide-react';

interface LogConfig {
    [key: string]: boolean;
}

interface PlanLimits {
    [key: string]: boolean;
}

interface LogConfigResponse {
    mode: 'platform' | 'customer';
    plan?: string;
    limits?: PlanLimits;
    config?: LogConfig;
    plan_definitions?: Record<string, PlanLimits>;
}

const LOG_FRIENDLY_NAMES: Record<string, string> = {
    "app_usage": "Application Usage Stats",
    "user_actions": "User Interactions & Clicks",
    "performance_metrics": "System Performance Metrics",
    "llm_prompts": "AI Prompts (Sensitive Data)",
    "llm_responses": "AI Responses (Sensitive Data)",
    "file_access_details": "Granular File Access Logs"
};

const LogConfigurationTable: React.FC = () => {
    const [data, setData] = useState<LogConfigResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [localConfig, setLocalConfig] = useState<LogConfig>({});

    // Fallback mock data for when API is unavailable
    const FALLBACK_CUSTOMER_DATA: LogConfigResponse = {
        mode: 'customer',
        plan: 'professional', // Default demo plan
        limits: {
            app_usage: true,
            user_actions: true,
            performance_metrics: true,
            llm_prompts: false,
            llm_responses: false,
            file_access_details: true
        },
        config: {
            app_usage: true,
            user_actions: true,
            performance_metrics: false,
            llm_prompts: false,
            llm_responses: false,
            file_access_details: false
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const result = await authenticatedFetchJSON<LogConfigResponse>('/api/admin/logs/config');
            setData(result);
            if (result.mode === 'customer' && result.config) {
                setLocalConfig(result.config);
            }
        } catch (error) {
            console.error("Failed to fetch log config, using fallback:", error);
            // Use fallback data for demo purposes
            setData(FALLBACK_CUSTOMER_DATA);
            setLocalConfig(FALLBACK_CUSTOMER_DATA.config || {});
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key: string) => {
        if (!data || data.mode !== 'customer') return;

        // Check limits
        if (!data.limits?.[key]) return; // Locked feature

        setLocalConfig(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSave = async () => {
        if (!data || data.mode !== 'customer') return;

        setSaving(true);
        try {
            await authenticatedFetchJSON('/api/admin/logs/config', {
                method: 'PUT',
                body: JSON.stringify({ enabled_logs: localConfig })
            });
            // Refresh to sync state
            await fetchConfig();
        } catch (error) {
            console.error("Failed to save config:", error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Configuration...</div>;

    if (!data) return <div className="p-8 text-center text-red-400">Failed to load configuration.</div>;

    // --- PLATFORM VIEW (AutoGain Admin) ---
    if (data.mode === 'platform' && data.plan_definitions) {
        const plans = ['starter', 'professional', 'enterprise'];
        const logTypes = Object.keys(data.plan_definitions['enterprise']);

        // Platform-level analytics categories
        const PLATFORM_ANALYTICS = {
            'Security & Compliance': [
                { id: 'customer_user_access', name: 'Customer User Access Logs', description: 'Track all user logins and session activity across customers', enabled: true },
                { id: 'customer_user_modifications', name: 'User Modifications', description: 'When customers add, remove, or modify users', enabled: true },
                { id: 'customer_admin_changes', name: 'Admin Role Changes', description: 'When new admins are added or removed', enabled: true },
            ],
            'AI & Usage': [
                { id: 'ai_agent_usage', name: 'AI Agent Usage', description: 'Calls per agent (Magnus Prime, FMEA Generator, etc.)', enabled: true },
                { id: 'token_consumption', name: 'Token Consumption', description: 'LLM tokens used per customer by agent', enabled: true },
                { id: 'app_popularity', name: 'App Usage Analytics', description: 'Which apps/features customers use most', enabled: true },
            ],
            'Revenue & Business Health': [
                { id: 'mrr_arr_tracking', name: 'MRR/ARR per Customer', description: 'Track monthly and annual recurring revenue per account', enabled: true },
                { id: 'plan_changes', name: 'Plan Upgrade/Downgrade Events', description: 'Identify upsell opportunities or churn risk', enabled: true },
                { id: 'trial_conversion', name: 'Trial → Paid Conversion', description: 'Marketing effectiveness and funnel analytics', enabled: true },
            ],
            'Product & Engagement': [
                { id: 'session_duration', name: 'Session Duration', description: 'Measure user engagement quality and stickiness', enabled: true },
                { id: 'feature_activation', name: 'Feature Activation Rate', description: 'Are customers using features they paid for?', enabled: true },
                { id: 'error_failure_rates', name: 'Error/Failure Rates', description: 'Proactive support - fix issues before tickets', enabled: true },
            ],
            'AI Cost Optimization': [
                { id: 'cost_per_ai_call', name: 'Cost per AI Call', description: 'Margin tracking by agent type', enabled: true },
                { id: 'cache_hit_rate', name: 'Cache Hit Rate', description: 'Token savings from intelligent caching', enabled: true },
                { id: 'response_quality_score', name: 'Response Quality Score', description: 'AI effectiveness based on user feedback', enabled: false },
            ],
        };

        // Rate limiting and alert thresholds
        const RATE_LIMIT_ALERTS = [
            { id: 'api_calls_per_hour', name: 'API Calls per Hour', threshold: 5000, unit: '/hr', severity: 'warning', description: 'Alert when customer exceeds hourly API call limit' },
            { id: 'ai_calls_per_hour', name: 'AI Agent Calls per Hour', threshold: 500, unit: '/hr', severity: 'critical', description: 'Alert on excessive AI calls (cost protection)' },
            { id: 'tokens_per_day', name: 'LLM Tokens per Day', threshold: 1000000, unit: '/day', severity: 'warning', description: 'Alert when daily token usage exceeds threshold' },
            { id: 'log_writes_per_hour', name: 'Log Writes per Hour', threshold: 10000, unit: '/hr', severity: 'critical', description: 'Detect logging loops or misconfigurations' },
            { id: 'failed_auth_per_hour', name: 'Failed Auth Attempts per Hour', threshold: 100, unit: '/hr', severity: 'critical', description: 'Potential brute force detection' },
            { id: 'error_rate_percent', name: 'Error Rate %', threshold: 5, unit: '%', severity: 'warning', description: 'Alert when error rate exceeds threshold' },
        ];

        return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <Shield className="text-blue-600" size={18} />
                        Platform Logging & Analytics (AutoGain Admin)
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        Configure platform-wide logging and define subscription tier limits.
                    </p>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {/* Platform Analytics Section - Organized by Category */}
                    {Object.entries(PLATFORM_ANALYTICS).map(([category, items]) => (
                        <div key={category} className="p-4 border-b border-slate-100">
                            <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">{category}</h4>
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-slate-100">
                                    {items.map(item => (
                                        <tr key={item.id} className="hover:bg-slate-50">
                                            <td className="py-2 pr-4">
                                                <div className="font-medium text-slate-700">{item.name}</div>
                                                <div className="text-xs text-slate-400">{item.description}</div>
                                            </td>
                                            <td className="py-2 w-24 text-center">
                                                <div className={`relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer ${item.enabled ? 'bg-blue-600' : 'bg-slate-300'}`}>
                                                    <span className={`${item.enabled ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}

                    {/* Rate Limits & Alerts Section */}
                    <div className="p-4 border-b border-slate-100 bg-red-50/30">
                        <h4 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                            ⚠️ Rate Limits & Alerts (Runaway Protection)
                        </h4>
                        <p className="text-xs text-slate-500 mb-3">Configure thresholds to detect bugs, misconfigurations, or abuse. Alerts trigger when limits are exceeded.</p>
                        <table className="w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="text-left py-2">Metric</th>
                                    <th className="text-center py-2 w-24">Threshold</th>
                                    <th className="text-center py-2 w-20">Severity</th>
                                    <th className="text-center py-2 w-20">Enabled</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {RATE_LIMIT_ALERTS.map(alert => (
                                    <tr key={alert.id} className="hover:bg-white">
                                        <td className="py-2">
                                            <div className="font-medium text-slate-700">{alert.name}</div>
                                            <div className="text-xs text-slate-400">{alert.description}</div>
                                        </td>
                                        <td className="py-2 text-center">
                                            <span className="font-mono text-sm font-bold text-slate-700">
                                                {alert.threshold.toLocaleString()}{alert.unit}
                                            </span>
                                        </td>
                                        <td className="py-2 text-center">
                                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${alert.severity === 'critical'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {alert.severity.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="py-2 text-center">
                                            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-red-500 cursor-pointer">
                                                <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition-transform" />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Subscription Tier Limits */}
                    <div className="p-4">
                        <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3">Customer Logging Limits by Plan</h4>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs">
                                <tr>
                                    <th className="px-4 py-2">Log Feature</th>
                                    {plans.map(plan => (
                                        <th key={plan} className="px-4 py-2 text-center capitalize">{plan}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {logTypes.map(key => (
                                    <tr key={key} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 font-medium text-slate-700">
                                            {LOG_FRIENDLY_NAMES[key] || key}
                                        </td>
                                        {plans.map(plan => {
                                            const allowed = data.plan_definitions![plan][key];
                                            return (
                                                <td key={`${plan}-${key}`} className="px-4 py-3 text-center">
                                                    {allowed ? (
                                                        <Check size={16} className="mx-auto text-emerald-500" />
                                                    ) : (
                                                        <X size={16} className="mx-auto text-slate-300" />
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                {/* Retention Row */}
                                <tr className="bg-slate-50 font-medium">
                                    <td className="px-4 py-3 text-slate-700">Log Retention Period</td>
                                    <td className="px-4 py-3 text-center text-slate-600">90 Days</td>
                                    <td className="px-4 py-3 text-center text-blue-600">1 Year</td>
                                    <td className="px-4 py-3 text-center text-indigo-600 font-bold">7 Years</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="p-3 bg-blue-50 text-xs text-blue-800 border-t border-blue-100 text-center">
                    Platform analytics are only visible to AutoGain staff. Customer data is aggregated and anonymized.
                </div>
            </div>
        );
    }

    // --- CUSTOMER VIEW ---
    const logTypes = Object.keys(data.limits || {});

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                <div>
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <Shield className="text-emerald-600" size={18} />
                        Audit Logging Configuration
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        Current Plan: <span className="font-bold uppercase text-emerald-600">{data.plan}</span>
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold shadow-sm transition-all disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            <div className="overflow-y-auto flex-1">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs sticky top-0">
                        <tr>
                            <th className="px-6 py-3 w-1/2">Log Feature</th>
                            <th className="px-6 py-3 w-1/4 text-center">Plan Access</th>
                            <th className="px-6 py-3 w-1/4 text-center">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {logTypes.map(key => {
                            const isAllowed = data.limits![key];
                            const isEnabled = localConfig[key] || false;

                            return (
                                <tr key={key} className={`transition-colors ${isAllowed ? 'hover:bg-slate-50' : 'bg-slate-50/50'}`}>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-slate-700">
                                            {LOG_FRIENDLY_NAMES[key] || key}
                                        </div>
                                        {key.includes('llm') && (
                                            <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                                                <Info size={10} /> May contain sensitive IP
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {isAllowed ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                                                <Unlock size={10} /> Available
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">
                                                <Lock size={10} /> Upgrade Req.
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center">
                                            <button
                                                onClick={() => handleToggle(key)}
                                                disabled={!isAllowed}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${isEnabled ? 'bg-emerald-600' : 'bg-slate-200'
                                                    } ${!isAllowed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                            >
                                                <span
                                                    className={`${isEnabled ? 'translate-x-6' : 'translate-x-1'
                                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {/* Retention Policy Row (Static based on Plan) */}
                        <tr className="bg-slate-50 border-t border-slate-200">
                            <td className="px-6 py-4">
                                <div className="font-medium text-slate-800 flex items-center gap-2">
                                    <Shield size={16} className="text-emerald-600" /> Audit Log Retention
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    Period that logs remain active before archival.
                                </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${data.plan === 'enterprise' ? 'bg-indigo-100 text-indigo-800' :
                                    data.plan === 'professional' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'
                                    }`}>
                                    {data.plan === 'enterprise' ? '7 Years' :
                                        data.plan === 'professional' ? '1 Year' : '90 Days'}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-center text-xs text-slate-500 italic">
                                Enforced by Plan
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LogConfigurationTable;
